import { Span } from "@chakra-ui/react"
import SearchCombobox, { ComboxActions } from "./SearchCombobox"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Link from "@hydrologiq/shared/components/Link"

export type AssetItem = {
  id: string
  name: string
}

interface AvailableAssetComboboxProps {
  startDate?: string
  endDate?: string
  onItemSelection?: (value: AssetItem | undefined) => void
  initialValue?: AssetItem | string // Support both object and string ID for backwards compatibility
  value?: AssetItem | null
  valueId?: string | null
  refreshTrigger?: number
  disabled?: boolean // Allow parent to disable the combobox
  Actions?: ComboxActions
  CreateAction?: () => React.ReactNode
  orderId?: string // Order ID to exclude from availability check
  excludedAssetIds?: string[] // Asset IDs to exclude from the list (already selected)
  clearAfterSelection?: boolean // If true, clears the input after selecting an item (default: false)
}

/**
 * Asset combobox that only shows assets available for a specific date range.
 * Requires both startDate and endDate to be provided to fetch available assets.
 * Uses the standard useSearchQuery hook for consistent search/cache behavior.
 */
const AvailableAssetCombobox = (props: AvailableAssetComboboxProps) => {
  const {
    startDate,
    endDate,
    onItemSelection,
    initialValue,
    value,
    valueId,
    refreshTrigger,
    disabled,
    Actions,
    CreateAction,
    orderId,
    excludedAssetIds = [],
    clearAfterSelection = false,
  } = props

  const hasValidDates = Boolean(startDate && endDate)

  // Handle initialValue being either an object or a string ID
  const initialAsset = typeof initialValue === "string" ? undefined : initialValue
  const initialValueId = typeof initialValue === "string" ? initialValue : valueId

  const defaultCreateAction = () => (
    <Link href="/assets" variant="primary">
      Create asset
    </Link>
  )

  // Use a ref to store the clear function from SearchCombobox
  const clearFnRef = useRef<(() => void) | null>(null)

  // Track refresh trigger to force dropdown update after clearing
  const [internalRefreshTrigger, setInternalRefreshTrigger] = useState(0)

  // Wrap onItemSelection to filter out excluded assets from selection and optionally clear after selection
  const handleItemSelection = useCallback(
    (item: AssetItem | undefined) => {
      // Don't allow selecting excluded assets
      if (item && excludedAssetIds.includes(item.id)) {
        return
      }

      // Call parent's onItemSelection
      onItemSelection?.(item)

      // Clear the combobox value after selection if enabled
      if (item && clearAfterSelection && clearFnRef.current) {
        // Use setTimeout to ensure the selection completes before clearing
        setTimeout(() => {
          clearFnRef.current?.()
          // Trigger a refresh to show the updated filtered list
          setInternalRefreshTrigger((prev) => prev + 1)
        }, 50)
      }
    },
    [onItemSelection, excludedAssetIds, clearAfterSelection]
  )

  // Filter excluded asset IDs from the query variables by using a custom filter string
  const excludeFilter = useMemo(() => {
    return excludedAssetIds.length > 0 ? excludedAssetIds.join(",") : undefined
  }, [excludedAssetIds])

  // Custom filter function to exclude already selected assets DURING typing
  const customFilter = useCallback(
    (item: AssetItem, searchTerm: string) => {
      // First check if item should be excluded
      if (excludedAssetIds.includes(item.id)) {
        return false
      }
      // Then apply search filter
      return item.name.toLowerCase().includes(searchTerm.toLowerCase())
    },
    [excludedAssetIds]
  )

  // Use refs to store functions from SearchCombobox
  const excludeFnRef = useRef<((id: string) => void) | null>(null)
  const unexcludeFnRef = useRef<((id: string) => void) | null>(null)
  // Track when the functions become available
  const [excludeFnReady, setExcludeFnReady] = useState(false)
  // Track refresh trigger for when excluded assets change
  const [exclusionRefreshTrigger, setExclusionRefreshTrigger] = useState(0)

  // Wrap the Actions to capture the clear, exclude, and unexclude functions
  const wrappedActions = useCallback(
    (actionsProps: { clear: () => void; exclude: (id: string) => void; unexclude: (id: string) => void }) => {
      // Store the functions for our hooks
      clearFnRef.current = actionsProps.clear
      excludeFnRef.current = actionsProps.exclude
      unexcludeFnRef.current = actionsProps.unexclude

      // Signal that the functions are ready
      if (!excludeFnReady) {
        setExcludeFnReady(true)
      }

      // If parent provided Actions, render them
      return Actions ? Actions(actionsProps) : null
    },
    [Actions, excludeFnReady]
  )

  // Trigger refresh when excluded assets change
  useEffect(() => {
    setExclusionRefreshTrigger((prev) => prev + 1)
  }, [excludedAssetIds])

  // Combine external and internal refresh triggers
  const combinedRefreshTrigger = useMemo(() => {
    return (refreshTrigger || 0) + internalRefreshTrigger + exclusionRefreshTrigger
  }, [refreshTrigger, internalRefreshTrigger, exclusionRefreshTrigger])

  // Keep SearchCombobox's internal excludedItems in sync with our excludedAssetIds
  // This ensures excluded items stay excluded even after refresh
  const prevExcludedAssetIdsRef = useRef<string[]>([])
  useEffect(() => {
    // Wait for functions to be set
    if (!excludeFnRef.current || !unexcludeFnRef.current) {
      return
    }

    const prevExcluded = prevExcludedAssetIdsRef.current
    const currentExcluded = excludedAssetIds

    // Find assets that were added to exclusion
    const newlyExcluded = currentExcluded.filter((id) => !prevExcluded.includes(id))

    // Find assets that were removed from exclusion (should be re-included)
    const newlyIncluded = prevExcluded.filter((id) => !currentExcluded.includes(id))

    // Exclude newly excluded assets
    newlyExcluded.forEach((id) => {
      excludeFnRef.current!(id)
    })

    // Unexclude newly included assets
    newlyIncluded.forEach((id) => {
      unexcludeFnRef.current!(id)
    })

    // Update the ref
    prevExcludedAssetIdsRef.current = [...currentExcluded]
  }, [excludedAssetIds, combinedRefreshTrigger, excludeFnReady]) // Also run when functions become ready

  return (
    <SearchCombobox<AssetItem>
      queryType="availableAssets"
      onItemSelection={handleItemSelection}
      initialValue={initialAsset}
      value={value}
      valueId={initialValueId}
      refreshTrigger={combinedRefreshTrigger}
      label="available assets"
      disabled={disabled || !hasValidDates}
      loadInitialItems={hasValidDates}
      customFilter={customFilter}
      initialExcludedItems={excludedAssetIds}
      additionalVariables={{
        start: startDate,
        end: endDate,
        per_page: 100,
        order_exclude: orderId, // Pass order ID to exclude from availability check
        exclude_asset_ids: excludeFilter, // Pass excluded asset IDs (if backend supports it)
      }}
      itemContent={(item) => (
        <Span width="100%" alignItems="start" textStyle="sm" fontWeight="medium">
          {item.name}
        </Span>
      )}
      CreateAction={CreateAction || defaultCreateAction}
      Actions={wrappedActions}
    />
  )
}

export default AvailableAssetCombobox
